import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Button,
  Card,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { priorityOptions, statusOptions } from '../config/data';
import {
  Comment,
  JobOption,
  TaskFormFieldsProps,
  SubTask
} from '../types/formFields';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTimes,
  faEdit,
  faSave,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAccessToken } from 'providers/AccessTokenProvider';
import { format } from 'date-fns';
import { UPDATE_SUBTASK_STATUS } from 'services/api/task';
import { GET_JOBSTAGE_QUERY } from 'services/api/jobStage';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';

type SubTaskWithNew = SubTask & { isNew?: boolean };

const TaskFormFields: React.FC<TaskFormFieldsProps> = ({
  form,
  employees,
  jobs,
  onJobSearch,
  jobSearchTerm,
  isEditMode = false,
  taskId
}) => {
  const [newSubTask, setNewSubTask] = useState<string>('');
  const [newComment, setNewComment] = useState<string>('');
  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(
    null
  );
  const [editCommentText, setEditCommentText] = useState<string>('');
  const { authUser } = useAccessToken();

  const {
    register,
    control,
    formState: { errors },
    setValue,
    getValues,
    watch
  } = form;

  // Ensure jobId is set if only one job is available
  useEffect(() => {
    if (jobs.length === 1 && !getValues('jobId')) {
      setValue('jobId', jobs[0].value, { shouldValidate: true });
    }
  }, [jobs, getValues, setValue]);

  const subTasks = watch('subTask') || [];

  // Job stages for the stage-based reminder control
  const { data: jobStages, isLoading: stagesLoading } = useQuery(
    'jobStages',
    GET_JOBSTAGE_QUERY,
    { staleTime: 5 * 60 * 1000 }
  );

  // When a stage-based reminder is configured, the task is triggered by job
  // stage changes rather than a due date — so Due Date/Time are not required.
  const reminderTrigger = watch('stageReminder.triggerType');
  const isStageBased = !!reminderTrigger && reminderTrigger !== 'none';

  const updateSubTaskMutation = useMutation(UPDATE_SUBTASK_STATUS, {
    onSuccess: () => {
      toast.success('Subtask status updated');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.messages || 'Failed to update subtask status'
      );
    }
  });

  useEffect(() => {
    if (jobs.length === 1 && !getValues('jobId')) {
      setValue('jobId', jobs[0].value, { shouldValidate: true });
    }
  }, [jobs, getValues, setValue]);

  const handleAddSubTask = () => {
    if (newSubTask.trim()) {
      const currentSubTasks = [...(getValues('subTask') || [])];
      currentSubTasks.push({
        text: newSubTask.trim(),
        completed: false,
        completedAt: null,
        completedBy: null,
        isNew: true
      } as any);
      setValue('subTask', currentSubTasks);
      setNewSubTask('');
    }
  };

  const handleRemoveSubTask = (index: number) => {
    const currentSubTasks = [...(getValues('subTask') || [])];
    currentSubTasks.splice(index, 1);
    setValue('subTask', currentSubTasks);
  };

  const handleToggleSubTask = (index: number) => {
    const currentSubTasks = [...(getValues('subTask') || [])];
    const isCompleted = !currentSubTasks[index].completed;

    currentSubTasks[index] = {
      ...currentSubTasks[index],
      completed: isCompleted,
      completedAt: isCompleted ? new Date().toISOString() : null,
      completedBy: isCompleted
        ? (authUser as any)?.id || authUser?._id || null
        : null
    };

    setValue('subTask', currentSubTasks);

    const allSubtasksCompleted = currentSubTasks.every(
      subtask => subtask.completed
    );
    setValue('completed', allSubtasksCompleted);

    if (isEditMode && taskId) {
      updateSubTaskMutation.mutate({
        taskId,
        subTaskIndex: index,
        completed: isCompleted,
        completedAt: isCompleted ? new Date().toISOString() : null,
        completedBy: isCompleted
          ? (authUser as any)?.id || authUser?._id || null
          : null
      });
    }
  };

  const handleAddComment = () => {
    if (newComment.trim()) {
      const currentComments = [...(getValues('comments') || [])];
      currentComments.push({
        text: newComment.trim(),
        userId: (authUser as any)?.id || authUser?._id || '',
        createdAt: new Date().toISOString()
      });
      setValue('comments', currentComments);
      setNewComment('');
    }
  };

  const startEditComment = (index: number, comment: Comment) => {
    setEditingCommentIndex(index);
    setEditCommentText(comment.text);
  };

  const saveEditedComment = () => {
    if (editingCommentIndex !== null && editCommentText.trim()) {
      const currentComments = [...(getValues('comments') || [])];
      currentComments[editingCommentIndex] = {
        ...currentComments[editingCommentIndex],
        text: editCommentText.trim(),
        editedAt: new Date().toISOString()
      };
      setValue('comments', currentComments);
      setEditingCommentIndex(null);
      setEditCommentText('');
    }
  };

  const cancelEditComment = () => {
    setEditingCommentIndex(null);
    setEditCommentText('');
  };

  const handleRemoveComment = (index: number) => {
    const currentComments = [...(getValues('comments') || [])];
    currentComments.splice(index, 1);
    setValue('comments', currentComments);
  };

  const renderStageReminder = () => {
    const selectedJobId = watch('jobId');
    // A stage-based reminder only makes sense for a job-associated task.
    if (!selectedJobId) return null;

    const triggerType = watch('stageReminder.triggerType');
    const stageOptions = (jobStages?.data || []).map(stage => ({
      value: stage.id,
      label: stage.name
    }));

    return (
      <Form.Group>
        <div className="d-flex align-items-center mb-1">
          <Form.Label className="mb-0 me-1">
            Reminder based on Job Stage
          </Form.Label>
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip id="stage-reminder-tooltip">
                Notify the task’s assignees and creator when this job changes
                stage. Choose “Any stage change” to be reminded on every change,
                or “Specific stage(s)” to be reminded only when the job enters
                the selected stage(s).
              </Tooltip>
            }
          >
            <span style={{ cursor: 'pointer', color: '#6c757d' }}>
              <FontAwesomeIcon icon={faInfoCircle} size="sm" />
            </span>
          </OverlayTrigger>
        </div>
        <Controller
          control={control}
          name="stageReminder.triggerType"
          render={({ field }) => (
            <div className="d-flex flex-wrap gap-3 mb-2">
              <Form.Check
                type="radio"
                id="stage-reminder-none"
                label="No reminder"
                checked={field.value === 'none'}
                onChange={() => field.onChange('none')}
              />
              <Form.Check
                type="radio"
                id="stage-reminder-any"
                label="Any stage change"
                checked={field.value === 'any_stage_change'}
                onChange={() => field.onChange('any_stage_change')}
              />
              <Form.Check
                type="radio"
                id="stage-reminder-specific"
                label="Specific stage(s)"
                checked={field.value === 'specific_stages'}
                onChange={() => field.onChange('specific_stages')}
              />
            </div>
          )}
        />
        {triggerType === 'specific_stages' && (
          <Controller
            control={control}
            name="stageReminder.stageIds"
            rules={{
              validate: (value: string[]) =>
                (value && value.length > 0) ||
                'Select at least one stage, or choose another reminder option'
            }}
            render={({ field }) => (
              <>
                <Select
                  isMulti
                  options={stageOptions}
                  isLoading={stagesLoading}
                  value={stageOptions.filter(o =>
                    (field.value || []).includes(o.value)
                  )}
                  onChange={selected =>
                    field.onChange((selected || []).map((s: any) => s.value))
                  }
                  classNamePrefix="stage-reminder-select"
                  placeholder="Select stage(s) that trigger the reminder"
                />
                {errors.stageReminder?.stageIds && (
                  <div className="text-danger mt-1 small">
                    {(errors.stageReminder.stageIds as any).message}
                  </div>
                )}
              </>
            )}
          />
        )}
      </Form.Group>
    );
  };

  const renderJobSelect = () => (
    <Form.Group>
      <Form.Label>
        {jobs.length === 1 ? 'Selected Job' : 'Job'}
        {jobs.length === 1 ? '' : '*'}
      </Form.Label>
      <Controller
        control={control}
        name="jobId"
        rules={{ required: 'Job is required' }}
        render={({ field }) => (
          <>
            <Select
              options={jobs}
              value={jobs.find(job => job.value === field.value) || null}
              onChange={selectedOption =>
                field.onChange(selectedOption?.value || '')
              }
              onInputChange={input => onJobSearch && onJobSearch(input)}
              inputValue={jobSearchTerm || ''}
              isSearchable={true}
              className="job-select"
              classNamePrefix="job-select"
              placeholder={
                jobs.length === 1
                  ? 'Job is pre-selected'
                  : 'Search jobs by name'
              }
              isDisabled={jobs.length === 1}
              formatOptionLabel={(option: JobOption) => (
                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center gap-2">
                    <div className="fw-bold">{option.label}</div>
                    {option.jobNumber && (
                      <span className="badge bg-primary text-white small">
                        #{option.jobNumber}
                      </span>
                    )}
                  </div>
                  <div className="d-flex flex-wrap gap-2 small text-muted">
                    {option.well && <span>Well: {option.well}</span>}
                    {option.location && (
                      <span>
                        Location:{' '}
                        {typeof option.location === 'string'
                          ? option.location
                          : option.location.location || 'N/A'}
                      </span>
                    )}
                  </div>
                </div>
              )}
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  backgroundColor: jobs.length === 1 ? '#f8f9fa' : 'white',
                  borderColor: errors.jobId ? 'red' : '#dee2e6'
                }),
                menu: baseStyles => ({
                  ...baseStyles,
                  backgroundColor: 'white',
                  zIndex: 1000
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isFocused ? '#f8f9fa' : 'white',
                  color: 'black',
                  padding: '10px'
                }),
                singleValue: baseStyles => ({
                  ...baseStyles,
                  color: 'black'
                }),
                input: baseStyles => ({
                  ...baseStyles,
                  color: 'black'
                })
              }}
            />
          </>
        )}
      />
      {errors.jobId && (
        <div className="text-danger mt-1 small">{errors.jobId.message}</div>
      )}
    </Form.Group>
  );

  const renderSubTasks = () => (
    <Form.Group>
      <div className="d-flex align-items-center mb-1">
        <Form.Label className="mb-0 me-1">Sub Tasks</Form.Label>
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip id="subtask-tooltip">
              A subtask is one or more line items that are in support to the
              main task and are required to be completed before the main task
              can be marked completed. Subtask are not required for main task
              functionality.
            </Tooltip>
          }
        >
          <span style={{ cursor: 'pointer', color: '#6c757d' }}>
            <FontAwesomeIcon icon={faInfoCircle} size="sm" />
          </span>
        </OverlayTrigger>
      </div>
      <div className="d-flex mb-2">
        <Form.Control
          type="text"
          placeholder="Add a sub task"
          value={newSubTask}
          onChange={e => setNewSubTask(e.target.value)}
          className="me-2"
        />
        <Button
          variant="outline-primary"
          onClick={handleAddSubTask}
          disabled={!newSubTask.trim()}
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>

      <div className="mt-2">
        <Controller
          control={control}
          name="subTask"
          render={({ field }) => (
            <div>
              {field.value && field.value.length > 0 ? (
                <div className="d-flex flex-column gap-2">
                  {field.value.map((subtask: SubTaskWithNew, index: number) => (
                    <div
                      key={index}
                      className={`d-flex align-items-center p-2 border rounded ${
                        subtask.completed ? 'bg-light' : ''
                      }`}
                    >
                      <Form.Check
                        type="checkbox"
                        checked={subtask.completed}
                        onChange={() => handleToggleSubTask(index)}
                        className="me-2"
                        disabled={!!subtask.isNew}
                      />
                      <span
                        className={`flex-grow-1 ${
                          subtask.completed
                            ? 'text-decoration-line-through text-muted'
                            : ''
                        }`}
                      >
                        {subtask.text}
                      </span>
                      {subtask.isNew && (
                        <small className="text-warning ms-2">
                          (Save task to enable)
                        </small>
                      )}
                      {subtask.completed && (
                        <small className="text-muted me-2">
                          {format(
                            new Date(subtask.completedAt || ''),
                            'MMM dd, yyyy HH:mm'
                          )}
                        </small>
                      )}
                      <Button
                        variant="link"
                        className="p-0 text-danger"
                        onClick={() => handleRemoveSubTask(index)}
                      >
                        <FontAwesomeIcon icon={faTimes} size="xs" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted">No sub tasks added</div>
              )}
            </div>
          )}
        />
      </div>
    </Form.Group>
  );

  return (
    <>
      <Row className="mb-3">
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Task Name*</Form.Label>
            <Form.Control
              {...register('task', { required: 'Task name is required' })}
              isInvalid={!!errors.task}
            />
            {errors.task && (
              <Form.Control.Feedback type="invalid">
                {errors.task.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12}>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              {...register('description', {
                required: 'Description is required'
              })}
              rows={3}
              className="form-control"
              isInvalid={!!errors.description}
            />
            {errors.description && (
              <Form.Control.Feedback type="invalid">
                {errors.description.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12}>{renderSubTasks()}</Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Due Date{isStageBased ? '' : '*'}</Form.Label>
            <Controller
              control={control}
              name="date"
              rules={{
                required: isStageBased ? false : 'Due date is required'
              }}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={date => field.onChange(date)}
                  className="form-control"
                  dateFormat="MM-dd-yyyy"
                  wrapperClassName="w-100"
                  isClearable
                  placeholderText={
                    isStageBased ? 'Optional' : 'Select a due date'
                  }
                />
              )}
            />
            {errors.date && (
              <div className="custom-task-danger small mt-1">
                {errors.date.message}
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Time{isStageBased ? '' : '*'}</Form.Label>
            <Form.Control
              type="time"
              {...register('time', {
                required: isStageBased ? false : 'Time is required'
              })}
              isInvalid={!!errors.time}
              className="form-control"
            />
            {errors.time && (
              <Form.Control.Feedback type="invalid">
                {errors.time.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Priority*</Form.Label>
            <Controller
              control={control}
              name="priority"
              rules={{ required: 'Priority is required' }}
              render={({ field }) => (
                <Form.Select {...field} isInvalid={!!errors.priority}>
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {errors.priority && (
              <Form.Control.Feedback type="invalid">
                {errors.priority.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Status*</Form.Label>
            <Controller
              control={control}
              name="status"
              rules={{ required: 'Status is required' }}
              render={({ field }) => (
                <Form.Select
                  {...field}
                  isInvalid={!!errors.status}
                  onChange={e => {
                    const newStatus = e.target.value;
                    field.onChange(newStatus);

                    // Auto-check completed checkbox when status is "completed" and no subtasks
                    if (
                      isEditMode &&
                      newStatus === 'completed' &&
                      subTasks.length === 0
                    ) {
                      setValue('completed', true);
                    }

                    // Uncheck completed checkbox when status changes from completed to in progress or not started
                    if (
                      isEditMode &&
                      getValues('completed') &&
                      (newStatus === 'in progress' ||
                        newStatus === 'not started')
                    ) {
                      setValue('completed', false);
                    }
                  }}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            />
            {errors.status && (
              <Form.Control.Feedback type="invalid">
                {errors.status.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>{renderJobSelect()}</Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>{renderStageReminder()}</Col>
      </Row>

      <Row className="mb-3">
        <Col xs={12}>
          <Form.Group controlId="assignedTo">
            <Form.Label>Assign To</Form.Label>
            <Controller
              control={control}
              name="assignedTo"
              render={({ field }) => (
                <Select
                  isMulti
                  options={employees.sort((a, b) =>
                    a.label.localeCompare(b.label)
                  )}
                  value={field.value}
                  onChange={selectedOptions =>
                    field.onChange(selectedOptions || [])
                  }
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={{
                    control: baseStyles => ({
                      ...baseStyles,
                      backgroundColor: 'bg-dark',
                      borderColor: '1px solid red'
                    }),
                    menu: baseStyles => ({
                      ...baseStyles,
                      backgroundColor: '#141824'
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isFocused ? 'bg-dark' : 'bg-dark',
                      color: 'white'
                    }),
                    multiValue: baseStyles => ({
                      ...baseStyles,
                      backgroundColor: '#141824'
                    }),
                    multiValueLabel: baseStyles => ({
                      ...baseStyles,
                      color: 'white'
                    }),
                    multiValueRemove: baseStyles => ({
                      ...baseStyles,
                      color: 'white',
                      ':hover': {
                        backgroundColor: 'bg-white',
                        color: 'white'
                      }
                    }),
                    input: baseStyles => ({
                      ...baseStyles,
                      color: 'white'
                    })
                  }}
                />
              )}
            />
          </Form.Group>
        </Col>
        {isEditMode && (
          <Col xs={12} className="pt-5">
            <Form.Group>
              <Form.Check
                type="checkbox"
                label="Mark as completed"
                {...register('completed')}
                disabled={
                  subTasks.length > 0 &&
                  !subTasks.every(subtask => subtask.completed)
                }
              />
            </Form.Group>
          </Col>
        )}
      </Row>

      {isEditMode && (
        <Row className="mb-3">
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Comments</Form.Label>
              <div className="mb-3">
                <Controller
                  control={control}
                  name="comments"
                  render={({ field }) => (
                    <div>
                      {field.value && field.value.length > 0 ? (
                        <div className="mb-3">
                          {field.value.map(
                            (comment: Comment, index: number) => (
                              <Card key={index} className="mb-2">
                                <Card.Body>
                                  {editingCommentIndex === index ? (
                                    <div>
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={editCommentText}
                                        onChange={e =>
                                          setEditCommentText(e.target.value)
                                        }
                                        className="mb-2"
                                      />
                                      <div className="d-flex gap-2">
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          onClick={saveEditedComment}
                                          disabled={!editCommentText.trim()}
                                        >
                                          <FontAwesomeIcon
                                            icon={faSave}
                                            className="me-1"
                                          />{' '}
                                          Save
                                        </Button>
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          onClick={cancelEditComment}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <p className="mb-1">{comment.text}</p>
                                      <div className="d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                          {comment.createdAt &&
                                            format(
                                              new Date(comment.createdAt),
                                              'MMM dd, yyyy HH:mm'
                                            )}
                                          {comment.editedAt && (
                                            <span className="ms-2">
                                              (edited:{' '}
                                              {format(
                                                new Date(comment.editedAt),
                                                'MMM dd, yyyy HH:mm'
                                              )}
                                              )
                                            </span>
                                          )}
                                        </small>
                                        <div className="d-flex gap-2">
                                          <Button
                                            variant="link"
                                            className="p-0 text-info"
                                            onClick={() =>
                                              startEditComment(index, comment)
                                            }
                                          >
                                            <FontAwesomeIcon
                                              icon={faEdit}
                                              size="sm"
                                            />
                                          </Button>
                                          <Button
                                            variant="link"
                                            className="p-0 text-danger"
                                            onClick={() =>
                                              handleRemoveComment(index)
                                            }
                                          >
                                            <FontAwesomeIcon
                                              icon={faTimes}
                                              size="sm"
                                            />
                                          </Button>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </Card.Body>
                              </Card>
                            )
                          )}
                        </div>
                      ) : (
                        <div className="text-muted mb-3">No comments yet</div>
                      )}
                    </div>
                  )}
                />

                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  className="mb-2"
                />
                <Button
                  variant="outline-primary"
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                >
                  Add Comment
                </Button>
              </div>
            </Form.Group>
          </Col>
        </Row>
      )}
    </>
  );
};

export default TaskFormFields;
